/**
 * LicenseGate.tsx — the license-acceptance gate.
 *
 * Renders a blocking modal over the whole app until the visitor accepts the
 * VSKI License. Acceptance is recorded in a cookie (`vski-license`), which
 * every API request then carries (patchFetch.ts sends cookies on all
 * fetches). The overlay is fully opaque — the app must not show through
 * behind a pending legal notice — and the modal is not dismissable: no
 * Escape, no backdrop click, no ✕. Only Accept closes it.
 */
import { useState } from 'react';
import { Modal } from '../panels/Modal';
import { LICENSE_TEXT, acceptLicense, licenseAccepted } from './license';

export function LicenseGate(): JSX.Element {
  // Initialise straight from the cookie so an accepted visitor never sees a
  // flash of the gate. Always rendered (Modal's `isOpen` owns mount/unmount,
  // per the Modal shell's contract) so the exit animation plays on Accept.
  const [open, setOpen] = useState(() => !licenseAccepted());

  return (
    <Modal
      title="VSKI License"
      size="wide"
      isOpen={open}
      dismissable={false}
      overlayClassName="modal-overlay--opaque"
      onClose={() => setOpen(false)}
      footer={
        <button
          type="button"
          className="btn btn-start"
          onClick={() => {
            acceptLicense();
            setOpen(false);
          }}
        >
          Accept
        </button>
      }
    >
      <textarea
        className="license-textarea"
        readOnly
        spellCheck={false}
        value={LICENSE_TEXT}
        aria-label="VSKI License text"
      />
    </Modal>
  );
}
